// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './LabelingRequests.scss';

import { useTranslation } from 'react-i18next';

import {
  collection,
  doc,
  query,
  where,
  getDocs,
  onSnapshot,
  limit,
  startAfter,
  getCountFromServer,
  or,
} from 'firebase/firestore';
import ImageSquare from '../../shared_components/ImageSquare/ImageSquare';

import checkmark_icon from '../assets/icons/checkmark.png';
import { OBSERVATION_DOCUMENT_PATH, OBSERVATIONS_COLLECTION_PATH } from '../utils/FirebaseUtils';

function getLabelingRequestQuery(firestore, client, userId) {
  /* Get the query for labeling requests (both general and user specific) */
  const requestsQuery = query(
    collection(firestore, OBSERVATIONS_COLLECTION_PATH(client)),
    or(
      // general labeling requests
      where('request_label', '==', true),
      // user specific labeling requests
      where('request_label_from_users', 'array-contains', userId),
    ),
  );
  return requestsQuery;
}

export async function getNumRequests(firestore, client, userId) {
  /* Get the number of requests */
  const requestsQuery = getLabelingRequestQuery(firestore, client, userId);
  const snapshot = await getCountFromServer(requestsQuery);
  return snapshot.data().count;
}

const LabelingRequests = React.forwardRef(
  ({ firestore, storage, currentUser, currentObservation, setCurrentObservation, paginationSamples = 20 }, ref) => {
    const { t } = useTranslation();
    const [observations, setObservations] = React.useState({});
    const [allLoaded, setAllLoaded] = React.useState(false);
    const [numRequests, setNumRequests] = React.useState(0);
    const lastSnapshot = React.useRef(null);
    const observationSubscription = React.useRef(null);

    async function loadObservations() {
      /* Load the next batch of observations with labeling requests */
      if (!currentUser.company.company) {
        return;
      }

      let observationQuery = query(
        getLabelingRequestQuery(firestore, currentUser.company.company, currentUser.uid),
        limit(paginationSamples),
      );

      // Data pagination: Get the next documents in line after the last one
      if (lastSnapshot.current) {
        observationQuery = query(observationQuery, startAfter(lastSnapshot.current));
      }

      const newObservations = {};
      const querySnapshot = await getDocs(observationQuery);
      querySnapshot.forEach((doc) => {
        lastSnapshot.current = doc;
        newObservations[doc.id] = { ...doc.data(), doc_id: doc.id };
      });
      setObservations((prevObservations) => {
        return {
          ...prevObservations,
          ...newObservations,
        };
      });

      // If no more documents are available with labeling requests, set the allLoaded flag
      if (querySnapshot.empty || querySnapshot.size < paginationSamples) {
        setAllLoaded(true);
      }

      // If you reached the last observation, close the fullscreen modal.
      // If the fullscreen modal is open, move to the next observation after loading.
      if (querySnapshot.empty) {
        setCurrentObservation(null);
      } else if (currentObservation) {
        setCurrentObservation(Object.values(newObservations)[0]);
      }
    }

    React.useEffect(() => {
      /* Initialize by loading first batch of documents and the number of requests */
      async function loadNumRequests() {
        if (!currentUser.company.company) {
          return;
        }
        setNumRequests(await getNumRequests(firestore, currentUser.company.company, currentUser.uid));
      }

      loadNumRequests();
      loadObservations();
    }, []);

    async function subscribeToObservation(docId) {
      /* Set up a listener on the doc-id to update on changes */
      // Unsubscribe from the previous listener
      if (observationSubscription.current) {
        observationSubscription.current();
      }

      // Subscribe to doc. On changes update the observation
      const observationUpdateQuery = query(
        doc(firestore, OBSERVATION_DOCUMENT_PATH(currentUser.company.company, docId)),
      );
      const unsubscribe = onSnapshot(observationUpdateQuery, (doc) => {
        const newObservation = { ...doc.data(), doc_id: doc.id };
        setObservations((prevObservations) => {
          return {
            ...prevObservations,
            [doc.id]: newObservation,
          };
        });
      });
      observationSubscription.current = () => unsubscribe();
    }

    React.useEffect(() => {
      /* Subscribe to the current observation when opened */
      if (currentObservation) {
        subscribeToObservation(currentObservation.doc_id);
      }
    }, [currentObservation]);

    function isObservationLabeled(observation) {
      /* Check if the observation is labeled */
      if (observation?.request_label) {
        return false;
      } else if (
        observation?.request_label_from_users &&
        observation.request_label_from_users.includes(currentUser.uid)
      ) {
        return false;
      }
      return true;
    }

    function getNextObservation(currentObservation) {
      /* Go the next observation. If last loaded observation is reached, load new ones */
      const nextIndex = Object.values(observations).findIndex((obj) => obj.doc_id === currentObservation.doc_id) + 1;
      if (nextIndex >= Object.values(observations).length) {
        if (!allLoaded) {
          loadObservations();
        } else {
          setCurrentObservation(false);
        }
      } else {
        setCurrentObservation(Object.values(observations)[nextIndex]);
      }
    }

    function getPreviousObservation(currentObservation) {
      /* Go the previous observation */
      const previousIndex =
        Object.values(observations).findIndex((obj) => obj.doc_id === currentObservation.doc_id) - 1;
      setCurrentObservation(Object.values(observations)[Math.max(previousIndex, 0)]);
    }

    React.useImperativeHandle(ref, () => ({
      getNextObservation,
      getPreviousObservation,
      getObservations: () => observations,
    }));

    return (
      <div className='labeling-requests'>
        <div className='labeling-list'>
          {Object.values(observations).map((observation) => (
            <div onClick={() => setCurrentObservation(observation)} key={observation.image_storage_path}>
              <ImageSquare
                storage={storage}
                imageStoragePath={observation.image_storage_path}
                icons={isObservationLabeled(observation) ? [checkmark_icon] : []}
              />
            </div>
          ))}
          {!allLoaded && (
            <div className='overflow-square' onClick={loadObservations}>
              <p className='overflow-number'>{`+${numRequests - Object.keys(observations).length}`}</p>
            </div>
          )}
        </div>
        {allLoaded && <p className='labeling--no-more-requests'>{t('labeling_no_more_requests')}</p>}
      </div>
    );
  },
);

LabelingRequests.displayName = 'LabelingRequests';

export default LabelingRequests;
